import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useSecurity } from "~/components/Hooks";
import { useAvailableJobTypes } from "~/repository/useAvailableJobTypes.js";
import { editTimeRecord as editTimeRecordAction } from "~/store/actions/actions.timeRecords.jsx";
import { deleteTimeRecord } from "~/store/actions/actions.timeRecords.jsx";
import { getAllJobTypesArray, getJobTypeById } from "~/store/selectors/selector.jobTypes.jsx";
import { getProjectIdByTaskId } from "~/store/selectors/selector.tasks.jsx";
import { getTimeRecordById } from "~/store/selectors/selector.timeRecords.jsx";
import { getLoggedUser } from "~/store/selectors/selector.users.jsx";
import TimeRecordFormListItem from "./TimeRecordFormListItem.jsx";
import { showErrorToast, showSuccessToast } from "../Toast/index.js";

const TimeRecordFormListItemContainer = ({ id, onEdit, onTimeRecordEdit, ...props }) => {
  const dispatch = useDispatch();
  const [trashConfirmation, setTrashConfirmation] = useState(false);
  const timeRecord = useSelector((state) => getTimeRecordById(state, id));
  const { t } = useTranslation();

  const jobType = useSelector((state) => getJobTypeById(state, timeRecord.job_type_id));

  const jobTypes = useSelector(getAllJobTypesArray);

  const loggedUser = useSelector(getLoggedUser);

  const { data } = useAvailableJobTypes({
    enabled: loggedUser.get("class") !== "Client",
  });

  const availableJobTypes = data
    ? jobTypes.filter((job) => data[timeRecord.user_id]?.includes(job.id) || job.id === jobType.id)
    : [];

  let projectId = useSelector((state) => getProjectIdByTaskId(state, timeRecord.parent_id));
  projectId = timeRecord.parent_type === "Project" ? timeRecord.parent_id : projectId;

  const can = useSecurity();
  const canEdit = can(["edit"], timeRecord);

  const onMoveToTrash = useCallback(() => {
    setTrashConfirmation(true);
  }, []);

  const closeTrashConfirmation = useCallback(() => {
    setTrashConfirmation(false);
  }, []);

  const moveToTrashCallback = useCallback(() => {
    dispatch(deleteTimeRecord(timeRecord.id)).then((action) => {
      if (action.error) {
        showErrorToast(t("Failed to move time record to trash"));
      } else {
        showSuccessToast(t("Time record moved to trash"));
      }
    });
    closeTrashConfirmation();
  }, [dispatch, timeRecord.id, closeTrashConfirmation, t]);

  const editTimeRecord = useCallback(
    (data) => {
      dispatch(editTimeRecordAction(projectId, timeRecord.id, data)).then((action) => {
        const { response } = action;
        const { entities = {}, result = {} } = response;
        const { single = 0 } = result;
        const { time_records = {} } = entities;
        if (typeof onTimeRecordEdit === "function" && single && time_records[single]) {
          onTimeRecordEdit(time_records[single]);
        }
      });
    },
    [dispatch, projectId, timeRecord, onTimeRecordEdit]
  );

  const onChangeBillableStatus = useCallback(
    (value) => {
      if (timeRecord.billableStatus !== value) {
        editTimeRecord(value);
      }
    },
    [timeRecord, editTimeRecord]
  );

  const onChangeJobType = useCallback(
    (value) => {
      const data = {
        id: timeRecord.id,
        job_type_id: value.job_type_id,
      };
      editTimeRecord(data);
    },
    [editTimeRecord, timeRecord.id]
  );

  const onChangeSummary = useCallback(
    ({ summary }) => {
      if (timeRecord.summary !== summary) {
        const data = {
          id: timeRecord.id,
          summary,
        };
        editTimeRecord(data);
      }
    },
    [timeRecord, editTimeRecord]
  );

  const onChangeRecordDate = useCallback(
    (value) => {
      editTimeRecord({
        id: timeRecord.id,
        ...value,
      });
    },
    [timeRecord, editTimeRecord]
  );

  const onChangeUser = useCallback(
    (value) => {
      if (timeRecord.userId !== value.user_id) {
        const data = {
          id: timeRecord.id,
          user_id: value.user_id,
        };
        editTimeRecord(data);
      }
    },
    [timeRecord, editTimeRecord]
  );

  const handleEditInputValue = useCallback(
    (value) => {
      const data = {
        id: timeRecord.id,
        value: value.value,
      };

      editTimeRecord(data);
      typeof onEdit === "function" && onEdit();
    },
    [timeRecord, editTimeRecord, onEdit]
  );

  return (
    <TimeRecordFormListItem
      {...props}
      timeRecord={timeRecord}
      canEdit={canEdit}
      onMoveToTrash={onMoveToTrash}
      jobTypes={availableJobTypes}
      onChangeBillableStatus={onChangeBillableStatus}
      onChangeJobType={onChangeJobType}
      jobTypeLabel={jobType.name}
      onEdit={handleEditInputValue}
      onChangeSummary={onChangeSummary}
      onChangeRecordDate={onChangeRecordDate}
      onChangeUser={onChangeUser}
      trashConfirmation={trashConfirmation}
      onConfirm={moveToTrashCallback}
      onCancel={closeTrashConfirmation}
    />
  );
};

TimeRecordFormListItemContainer.propTypes = {
  id: PropTypes.number,
  onEdit: PropTypes.func,
  onTimeRecordEdit: PropTypes.func,
  onTimeRecordTrash: PropTypes.func,
};

TimeRecordFormListItemContainer.displayName = "TimeRecordFormListItemContainer";

export default TimeRecordFormListItemContainer;
